import FilterMobilePopup from 'components/CategoryPage/FilterMobilePopup/FilterMobilePopup';
import HelloBar from 'components/CategoryPage/HelloBar/HelloBar';
import ItemsList from 'components/CategoryPage/ItemsList/ItemsList';
import SelectCategoryPopup from 'components/CategoryPage/SelectCategoryPopup/SelectCategoryPopup';
import Sidebar from 'components/CategoryPage/Sidebar/Sidebar';
import Backdrop from 'components/Common/Backdrop/Backdrop';
import Banner from 'components/Common/Banner/Banner';
import Spacer from 'components/Common/Spacer/Spacer';
import StudioSelectionSearchBar from 'components/Common/StudioSelectionSearchBar/StudioSelectionSearchBar';
import Layout from 'components/Layout/Layout';
import useCategoryURLParams from 'hooks/useCategoryURLParams';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Route } from 'routes';
import { AppDispatch, Store } from 'store';
import { getIsGettySearchEnabled, getIsStudioSelectionCategory } from 'store/app/selector/getConfigurationByKey';
import isDesktop from 'store/app/selector/isDesktop';
import fetchCurrentCategoryOperation from 'store/category/operation/fetchCurrentCategoryOperation';
import fetchDesignsOperation from 'store/category/operation/fetchDesignsOperation';
import fetchGTMProductsDataForCurrentDesignsOperation from 'store/category/operation/fetchGTMProductsDataForCurrentDesignsOperation';
import preserveCategoryFiltersOperation from 'store/category/operation/preserveCategoryFiltersOperation';
import setParamsFromQueryStringOperation from 'store/category/operation/setParamsFromQueryStringOperation';
import setParamsFromQueryStringServerOperation from 'store/category/operation/setParamsFromQueryStringServerOperation';
import getCurrentCategory from 'store/category/selector/getCurrentCategory';
import getFetchedCategoryId from 'store/category/selector/getFetchedCategoryId';
import { resetToSingleCategoryAction, setCategoryIdAction, setCategoryRefetchFlagAction } from 'store/category/slice';
import fetchPricesOperation from 'store/price/operation/fetchPricesOperation';
import SSRParams from 'utils/class/SSRParams';
import gtmSendFunnelStepEvent, { FUNNEL_STEPS } from 'utils/gtm/gtmSendFunnelStepEvent';
import removeGetParams from 'utils/routes/removeGetParams';
import styles from './CategoryPage.module.scss';

export interface OwnProps {
  route: Route;
}

const CategoryPage = ({ route }: OwnProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const { categoryId } = route;
  const isDesktopDevice = useSelector(isDesktop);
  const fetchedCategoryId = useSelector(getFetchedCategoryId);
  const isGettySearchEnabled = useSelector(getIsGettySearchEnabled);
  const breadCrumbs = useSelector(getCurrentCategory)?.breadcrumbs;
  const [isCtegoryDataValid, setIsCategoryDataValid] = useState(false);
  const helloBar = useSelector(getCurrentCategory)?.helloBar;
  const [isShowFilters, setIsShowFilters] = useState(true);

  const isStudioSelection = useSelector((store: Store) =>
    getIsStudioSelectionCategory(store, Number(fetchedCategoryId)),
  );
  const urlParams = useCategoryURLParams(1);
  const trackLocationChange = useRef<boolean>(false);
  const location = useLocation();

  useEffect(() => {
    if (!isCtegoryDataValid || !breadCrumbs) return;
    let extraTag = '/private/';
    const parentUrl = breadCrumbs.slice(-1)[0].url;
    extraTag += breadCrumbs ? `${removeGetParams(parentUrl)}` : '';
    extraTag = extraTag.replaceAll('//', '/');
    gtmSendFunnelStepEvent(FUNNEL_STEPS.category, extraTag);
  }, [isCtegoryDataValid]);

  useEffect(() => {
    dispatch(setCategoryIdAction(categoryId));
    dispatch(resetToSingleCategoryAction(categoryId));
    // price fetch will not happen on the server
    dispatch(fetchPricesOperation());

    if (fetchedCategoryId !== categoryId) {
      setIsCategoryDataValid(false);
      dispatch(setCategoryRefetchFlagAction(true));
      // TODO: remove unknown from next line
      dispatch(fetchCurrentCategoryOperation(true)).then(() => {
        setIsCategoryDataValid(true);
        dispatch(preserveCategoryFiltersOperation(urlParams.queryString));
        trackLocationChange.current = true;

        dispatch(fetchDesignsOperation()).then(() => {
          dispatch(fetchPricesOperation());
        });
      });
    } else {
      setIsCategoryDataValid(true);
    }
  }, []);

  useEffect(() => {
    if (trackLocationChange.current) {
      const queryString = new URLSearchParams(location.search);
      dispatch(setParamsFromQueryStringOperation(queryString));
    }
    trackLocationChange.current = true;
  }, [location]);

  return (
    <>
      <Layout>
        <Spacer />
        <HelloBar cleanupContentStyles={!isDesktopDevice} />
        {isStudioSelection && isGettySearchEnabled && (
          <StudioSelectionSearchBar categoryId={fetchedCategoryId} className={styles.searchBar} />
        )}
        <Banner />
        <Spacer x={2} />
        <div className={styles.categoryPageContent}>
          {isDesktopDevice && isShowFilters && <Sidebar />}
          <ItemsList onToggleFilters={() => setIsShowFilters(!isShowFilters)} isFiltersOpen={isShowFilters} />
        </div>
        <Backdrop />
        <SelectCategoryPopup />
        <FilterMobilePopup />
      </Layout>
    </>
  );
};

export default CategoryPage;

const fetchCategoryData = (params: SSRParams) => [
  params.dispatch(fetchCurrentCategoryOperation()),
  params.dispatch(fetchDesignsOperation()),
];

// Executed on the server to prepare initial data
export const fetchCategoryPageInitialData = (params: SSRParams): Promise<any>[] => {
  params.dispatch(setParamsFromQueryStringServerOperation(params.req.query));
  // Should return array of promises to resolve on the server
  return fetchCategoryData(params);
};
